// ---------------------------------------------------------------------------
// Base validator factories
// ---------------------------------------------------------------------------

/**
 * Requires a non-empty, non-whitespace string value.
 */
export const trimRequired = message => ({
  validator: (_, value) => {
    if (!value || !String(value).trim()) {
      return Promise.reject(new Error(message))
    }

    return Promise.resolve()
  },
})

/**
 * Requires the value to contain digits only (no spaces, no special chars).
 * Passes silently when the field is empty (pair with a `required` rule first).
 */
export const digitsOnlyValidator = message => ({
  validator: (_, value) => {
    if (!value) return Promise.resolve()
    if (!/^\d+$/.test(String(value).trim())) {
      return Promise.reject(new Error(message))
    }

    return Promise.resolve()
  },
})

/**
 * Requires the value to be a positive integer within [min, max].
 * Passes silently when the field is empty (pair with a `required` rule first).
 */
export const rangeValidator = (min, max, message) => ({
  validator: (_, value) => {
    if (!value) return Promise.resolve()
    const trimmed = String(value).trim()
    if (!/^\d+$/.test(trimmed)) {
      return Promise.reject(new Error(message))
    }
    const num = Number(trimmed)
    if (num < min || num > max) {
      return Promise.reject(new Error(message))
    }

    return Promise.resolve()
  },
})

/**
 * Requires the value to be at most `max` characters long.
 * Passes silently when the field is empty (pair with a `required` rule first).
 */
export const maxLengthValidator = (max, message) => ({
  validator: (_, value) => {
    if (!value) return Promise.resolve()
    if (String(value).trim().length > max) {
      return Promise.reject(new Error(message))
    }

    return Promise.resolve()
  },
})

/**
 * Validates IMEI numbers: can be a single IMEI (15 digits) or multiple IMEIs
 * separated by commas. Each IMEI must be exactly 15 digits.
 * Passes silently when the field is empty (pair with a `required` rule first).
 */
export const imeiValidator = t => ({
  validator: (_, value) => {
    if (!value) return Promise.resolve()

    const trimmed = String(value).trim()
    // Split by comma and trim each IMEI
    const imeis = trimmed
      .split(',')
      .map(imei => imei.trim())

    // Check if all IMEIs are valid (1 to 15 digits each)
    for (const imei of imeis) {
      if (!/^\d{1,15}$/.test(imei)) {
        return Promise.reject(
          new Error(t('deviceManagement.registerDrawer.imeiInvalid'))
        )
      }
    }

    return Promise.resolve()
  },
})

// ---------------------------------------------------------------------------
// Field rule sets
// Each function receives `t` (i18next translate fn) and returns an Ant Design
// `rules` array ready to be spread onto a <Form.Item>.
//
// Range rationale
//   hexnodeId     : Hexnode MDM numeric IDs stay well within 1 – 9 999 999 999
//   hologramId    : Hologram SIM IDs are positive integers, same ceiling
//   hologramIccId : ICCID is up to 22 decimal digits (E.164 / ITU-T E.118)
//   deviceCode    : Free-form reference, max 50 chars
//   name          : Display name, max 100 chars
//   comments      : Free-form text, max 500 chars
// ---------------------------------------------------------------------------

export const vesselRules = t => [
  {
    message: t('deviceManagement.registerDrawer.vesselRequired'),
    required: true,
  },
]

export const deviceCodeRules = t => [
  trimRequired(t('deviceManagement.registerDrawer.deviceCodeNameRequired')),
  maxLengthValidator(50, 'Device code must be 50 characters or fewer'),
]

export const deviceFriendlyNameRules = t => [
  trimRequired(t('deviceManagement.registerDrawer.deviceFriendlyNameRequired')),
  maxLengthValidator(100, 'Device name must be 100 characters or fewer'),
]

export const enrollmentDateRules = t => [
  {
    message: t('deviceManagement.registerDrawer.enrollmentDateRequired'),
    required: true,
  },
]

export const statusRules = t => [
  {
    message: t('deviceManagement.registerDrawer.statusRequired'),
  },
]

export const imeiNumbersRules = t => [
  {
    message: t('deviceManagement.registerDrawer.imeiNumbersRequired'),
    required: true,
  },
  imeiValidator(t),
]

export const hexnodeRefIdRules = t => [
  {
    message: t('deviceManagement.registerDrawer.hexnodeRefIdRequired'),
    required: true,
  },
  rangeValidator(
    1,
    9_999_999_999,
    'Hexnode ID must be a positive integer (digits only, 1 to 10 digits)'
  ),
]

export const hologramIccidRules = t => [
  {
    message: t('deviceManagement.registerDrawer.hologramIccidRequired'),
    required: true,
  },
  // ICCID: 1 – 22 digit decimal string (ITU-T E.118). Use digit-length check
  // instead of numeric comparison to avoid JS precision loss beyond 15 digits.
  digitsOnlyValidator('Hologram ICCID must contain digits only'),
  {
    validator: (_, value) => {
      if (!value) return Promise.resolve()
      const trimmed = String(value).trim()
      if (trimmed.length < 1 || trimmed.length > 22) {
        return Promise.reject(
          new Error('Hologram ICCID must be 1 to 22 digits')
        )
      }

      return Promise.resolve()
    },
  },
]

export const hologramSimIdRules = t => [
  {
    message: t('deviceManagement.registerDrawer.hologramSimIdRequired'),
    required: true,
  },
  rangeValidator(
    1,
    9_999_999_999,
    'Hologram SIM ID must be a positive integer (digits only, 1 to 10 digits)'
  ),
]

export const commentsRules = t => [
  trimRequired(t('deviceManagement.registerDrawer.commentsRequired')),
  maxLengthValidator(500, 'Comments must be 500 characters or fewer'),
]

// Edit-mode rule sets reuse the same validators but source messages from the
// actionDrawer namespace where labels differ.

export const editDeviceFriendlyNameRules = t => [
  trimRequired(t('deviceManagement.actionDrawer.deviceFriendlyNameRequired')),
  maxLengthValidator(100, 'Device name must be 100 characters or fewer'),
]

export const editVesselRules = t => [
  {
    message: t('deviceManagement.actionDrawer.vesselNameRequired'),
    required: true,
  },
]

export const editDeviceCodeRules = t => [
  trimRequired(t('deviceManagement.registerDrawer.deviceCodeNameRequired')),
  maxLengthValidator(50, 'Device code must be 50 characters or fewer'),
]

export const editEnrollmentDateRules = t => [
  {
    message: t('deviceManagement.registerDrawer.enrollmentDateRequired'),
    required: true,
  },
]

export const editStatusRules = t => [
  {
    message: t('deviceManagement.registerDrawer.statusRequired'),
  },
]

export const editHexnodeRefIdRules = t => [
  {
    message: t('deviceManagement.registerDrawer.hexnodeRefIdRequired'),
    required: true,
  },
  rangeValidator(
    1,
    9_999_999_999,
    'Hexnode ID must be a positive integer (digits only, 1 to 10 digits)'
  ),
]

export const editHologramIccidRules = t => [
  {
    message: t('deviceManagement.registerDrawer.hologramIccidRequired'),
    required: true,
  },
  digitsOnlyValidator('Hologram ICCID must contain digits only'),
  {
    validator: (_, value) => {
      if (!value) return Promise.resolve()
      const trimmed = String(value).trim()
      if (trimmed.length < 1 || trimmed.length > 22) {
        return Promise.reject(
          new Error('Hologram ICCID must be 1 to 22 digits')
        )
      }

      return Promise.resolve()
    },
  },
]

export const editHologramSimIdRules = t => [
  {
    message: t('deviceManagement.registerDrawer.hologramSimIdRequired'),
    required: true,
  },
  rangeValidator(
    1,
    9_999_999_999,
    'Hologram SIM ID must be a positive integer (digits only, 1 to 10 digits)'
  ),
]

export const editCommentsRules = t => [
  trimRequired(t('deviceManagement.registerDrawer.commentsRequired')),
  maxLengthValidator(500, 'Comments must be 500 characters or fewer'),
]

export const editImeiNumbersRules = t => [
  {
    message: t('deviceManagement.registerDrawer.imeiNumbersRequired'),
    required: true,
  },
  imeiValidator(t),
]
